import _ from 'lodash';

import countries from '../../../../core/analytics/countries';
import states from '../../../../core/analytics/states';
import { HOUSEHOLD_INCOME_LABELS } from './constants';

const getCompanySizeName = (companySize) => {
  switch (companySize) {
    case 'Self-employed':
      return 'Sole Proprietorship: 1';

    case '1-10':
    case '11-50':
      return 'Small Business: 2-50';

    case '51-200':
    case '201-500':
    case '501-1000':
      return 'Medium Business: 51-1,000';

    case '1001-5000':
    case '5001-10,000':
      return 'Large Business: 1,001-10,000';

    case '10,001+':
      return 'Enterprise: 10,001+';

    default:
      return 'Unknown';
  }
};

export const filterAnalyticsImportantQuotes = (analytics) => {
  const quotes = _.filter(analytics, { important: true, removed: false });

  const positives = _.chain(quotes)
    .filter({ sentimentBySentence: 'Positive' })
    .take(5)
    .value();

  const negatives = _.chain(quotes)
    .filter({ sentimentBySentence: 'Negative' })
    .take(4)
    .value();

  const neutrals = _.chain(quotes)
    .filter({ sentimentBySentence: 'Neutral' })
    .take(2)
    .value();

  return [
    ...positives,
    ...negatives,
    ...neutrals,
  ];
};

export const analyticsLabels = (analytics) => _.map(analytics, ({ label }) => {
  if (!label) {
    return 'Unknown';
  }

  return _.truncate(label, {
    length: 15,
  });
});

export const analyticsToBarChart = (analytics, backgroundColor) => {
  const labels = analyticsLabels(analytics);
  const data = _.map(analytics, ({ qty }) => qty);

  return {
    labels,
    datasets: [{
      hidden: false,
      backgroundColor,
      data,
    }],
  };
};

export const analyticsAgeToBarChart = (analytics, backgroundColor) => {
  const labels = ['18-20', '21-29', '30-39', '40-54', '55+'];

  return analyticsToBarChart(labels.map((label) => {
    const data = _.find(analytics, { label });

    return {
      label,
      qty: data ? data.qty : 0,
    };
  }), backgroundColor);
};

export const analyticsToPieChart = (analytics) => {
  const slice = _.take(analytics, 6);

  const labels = analyticsLabels(slice);
  const data = _.map(slice, ({ qty }) => qty);

  const backgroundColor = ['#52709D', '#98E7D4', '#5CD0F7', '#A7BDF8', '#FF6666', '#999999'];

  return {
    labels,
    datasets: [{
      data,
      backgroundColor,
    }],
  };
};

export const analyticsToListChart = (analytics) => {
  const slice = _.take(analytics, 5);

  return _.map(slice, ({ label, qty: number }, index) => {
    const text = !label ? 'Unknown' : label;

    return ({
      id: `${text}-${index + 1}`,
      number,
      text,
    });
  });
};

export const analyticsLocationToBarChart = (analytics, backgroundColor) => analyticsToBarChart(
  analytics.map((data) => {
    let label = states[data.label];

    if (!label) {
      label = countries[data.label];
    }

    return ({
      ...data,
      label,
    });
  }),
  backgroundColor,
);

export const analyticsHouseholdIncomeToBarChart = (analytics, backgroundColor) => {
  const labels = HOUSEHOLD_INCOME_LABELS;

  return analyticsToBarChart(labels.map((label) => {
    const data = _.find(analytics, { label: label.data });

    return {
      label: label.display,
      qty: data ? data.qty : 0,
    };
  }), backgroundColor);
};

export const analyticsCompanySizeToListChart = (analytics) => {
  const named = _.chain(analytics)
    .map(({ label, qty }) => {
      const text = getCompanySizeName(label);

      return ({
        label: text,
        qty,
      });
    })
    .groupBy('label')
    .map((group) => ({
      label: _.first(group).label,
      qty: _.reduce(group, (qty, current) => qty + current.qty, 0),
    }))
    .orderBy(['qty', 'label'], ['desc', 'asc'])
    .take(5)
    .value();

  return _.map(named, ({ label, qty: number }, index) => ({
    id: `${label}-${index + 1}`,
    number,
    text: label,
  }));
};
